// src/components/CircuitPicker.jsx
// Scelta del circuito per gara e allenamento. Con centinaia di tracciati una
// griglia di schede diventa ingestibile: qui c'è una ricerca, i circuiti usati
// di recente e una lista compatta che scorre, con la sagoma del tracciato.
// Scelto il circuito, il pannello si chiude e resta una riga di riepilogo.
//   circuits  lista da GET /api/circuits
//   value     id selezionato · onChange(id)
//   tone      'red' (gara) | 'lime' (allenamento)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';
import { getSession } from '../session';
import './CircuitPicker.css';

const RECENT_MAX = 6;
const recentKey = () => `rs_recent_circuits_${getSession()?.instance?.id || 'racesense'}`;
const readRecent = () => {
  try { const v = JSON.parse(localStorage.getItem(recentKey()) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};
const pushRecent = (id) => {
  try {
    const next = [id, ...readRecent().filter(x => x !== id)].slice(0, RECENT_MAX);
    localStorage.setItem(recentKey(), JSON.stringify(next));
    return next;
  } catch { return readRecent(); }
};

// minuscole senza accenti, per una ricerca tollerante
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const fmtLen = (m) => (m ? (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`) : '—');
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(new Date(iso)); } catch { return ''; }
};

export function TrackThumb({ outline, size = 40 }) {
  if (!Array.isArray(outline) || outline.length < 3) {
    return <span className="cp-thumb cp-thumb--empty" style={{ width: size, height: size }}><Icon.Track size={Math.round(size * 0.45)} /></span>;
  }
  const pad = 3;
  const k = size - pad * 2;
  const d = outline.map(([x, y], i) => `${i ? 'L' : 'M'}${(pad + x * k).toFixed(1)} ${(pad + y * k).toFixed(1)}`).join(' ') + ' Z';
  return (
    <svg className="cp-thumb" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const SORTS = [
  { key: 'recent', label: 'Più recenti' },
  { key: 'name', label: 'A–Z' },
  { key: 'length', label: 'Lunghezza' },
];

// Memo: le pagine di setup si aggiornano di continuo (dispositivi in arrivo);
// l'elenco dei circuiti si ridisegna solo se cambiano i suoi dati.
export default React.memo(CircuitPicker);

function CircuitPicker({ circuits = [], value, onChange, tone = 'red', emptyText }) {
  const [open, setOpen] = useState(!value);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [recent, setRecent] = useState(readRecent);
  const [active, setActive] = useState(0);
  const [focusOnOpen, setFocusOnOpen] = useState(false); // solo se aperto dall'utente
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = circuits.find(c => c.id === value) || null;
  useEffect(() => { if (!value) setOpen(true); }, [value]);
  useEffect(() => { if (open && focusOnOpen) inputRef.current?.focus({ preventScroll: true }); }, [open, focusOnOpen]);

  const results = useMemo(() => {
    const words = norm(query).split(/\s+/).filter(Boolean);
    let list = circuits.filter(c => {
      if (!words.length) return true;
      const hay = norm(`${c.name} ${c.id}`);
      return words.every(w => hay.includes(w));
    });
    if (sort === 'name') list = [...list].sort((a, b) => String(a.name).localeCompare(String(b.name), 'it'));
    else if (sort === 'length') list = [...list].sort((a, b) => (b.lengthMeters || 0) - (a.lengthMeters || 0));
    return list;
  }, [circuits, query, sort]);

  const recentCircuits = useMemo(
    () => recent.map(id => circuits.find(c => c.id === id)).filter(Boolean),
    [recent, circuits]
  );

  useEffect(() => { setActive(0); }, [query, sort]);
  // tiene in vista la riga attiva durante la navigazione da tastiera
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const choose = (id) => {
    onChange(id);
    setRecent(pushRecent(id));
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(results.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[active]) choose(results[active].id); }
    else if (e.key === 'Escape' && selected) { e.preventDefault(); setOpen(false); setQuery(''); }
  };

  if (circuits.length === 0) {
    return (
      <div className="rs-empty">
        <Icon.Track />
        <strong>Nessun circuito disponibile</strong>
        <p>{emptyText || 'Crea un tracciato dalla sezione Circuiti.'}</p>
      </div>
    );
  }

  return (
    <div className={`cp cp--${tone}`}>
      {selected && !open && (
        <div className="cp-selected">
          <TrackThumb outline={selected.outline} size={56} />
          <div className="cp-selected__text">
            <span className="cp-label">Circuito selezionato</span>
            <strong className="cp-selected__name">{selected.name}</strong>
            <span className="cp-meta">
              {fmtLen(selected.lengthMeters)}
              {selected.widthMeters ? ` · largh. ${selected.widthMeters} m` : ''}
              {selected.corners ? ` · ${selected.corners} curve` : ''}
              {selected.readOnly ? ' · libreria' : ''}
            </span>
          </div>
          <button type="button" className="rs-btn rs-btn--sm" onClick={() => { setFocusOnOpen(true); setOpen(true); }}>Cambia</button>
        </div>
      )}

      {open && (
        <div className="cp-panel">
          <div className="cp-toolbar">
            <label className="cp-search">
              <Icon.Search size={15} />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={`Cerca tra ${circuits.length} circuiti…`}
                aria-label="Cerca circuito"
                aria-controls="cp-list"
                aria-activedescendant={results[active] ? `cp-opt-${active}` : undefined}
              />
              {query && <button type="button" className="cp-clear" onClick={() => setQuery('')} aria-label="Cancella ricerca"><Icon.X size={14} /></button>}
            </label>
            <div className="cp-sort" role="group" aria-label="Ordina">
              {SORTS.map(s => (
                <button key={s.key} type="button" className={sort === s.key ? 'is-active' : ''} onClick={() => setSort(s.key)}>{s.label}</button>
              ))}
            </div>
            {selected && <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => { setOpen(false); setQuery(''); }}>Annulla</button>}
          </div>

          {!query && recentCircuits.length > 0 && (
            <div className="cp-recent">
              <span className="cp-label">Usati di recente</span>
              <div className="cp-recent__list">
                {recentCircuits.map(c => (
                  <button key={c.id} type="button" className={`cp-chip${c.id === value ? ' is-selected' : ''}`} onClick={() => choose(c.id)} title={c.name}>
                    <TrackThumb outline={c.outline} size={20} />
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="cp-list" id="cp-list" role="listbox" ref={listRef} aria-label="Circuiti">
            {results.length === 0 ? (
              <div className="cp-none">Nessun circuito corrisponde a «{query}».</div>
            ) : results.map((c, i) => (
              <button
                key={c.id}
                id={`cp-opt-${i}`}
                data-idx={i}
                type="button"
                role="option"
                aria-selected={c.id === value}
                className={`cp-row${c.id === value ? ' is-selected' : ''}${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(c.id)}
              >
                <TrackThumb outline={c.outline} size={36} />
                <span className="cp-row__name">{c.name}{c.readOnly && <em className="cp-row__lib">Libreria</em>}</span>
                <span className="cp-row__num">{fmtLen(c.lengthMeters)}</span>
                <span className="cp-row__num cp-row__hide-sm">{c.corners ? `${c.corners} curve` : '—'}</span>
                <span className="cp-row__num cp-row__hide-sm">{fmtDate(c.createdAt)}</span>
                <span className="cp-row__check" aria-hidden="true">{c.id === value && <Icon.Check size={14} />}</span>
              </button>
            ))}
          </div>
          <div className="cp-foot">
            <span>{query ? `${results.length} di ${circuits.length} circuiti` : `${circuits.length} circuiti`}</span>
            <span className="cp-foot__keys">↑ ↓ per scorrere · Invio per scegliere</span>
          </div>
        </div>
      )}
    </div>
  );
}
